import React from 'react';

import {
  Box,
  VStack,
  Flex,
  HStack,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  InputLeftAddon,
  Select,
  Button,
  IconButton,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';

import { SearchIcon } from '@chakra-ui/icons';
import { BsFilter } from 'react-icons/bs';
import { MdDelete } from 'react-icons/md';

import { useColors } from './colors';

const FilterSection = ({
  searchField,
  setSearchField,
  searchQuery,
  setSearchQuery,
  handleSearch,
  filters,
  handleFilterChange,
  showFilters,
  setShowFilters,
  showDeleteSection,
  setShowDeleteSection,
  deleteFilters,
  handleDeleteFilterChange,
  handleDeleteByFilter,
  marketplaceOptions,
  storeOptions,
  supplierOptions,
  getMarketplacesListStatus,
  getStoresListStatus,
  getSuppliersListStatus,
  isDeleting,
}) => {
  const {
    borderColor,
    textColor,
    inputColor,
    placeholderColor,
    filterSectionBg,
    filterBg,
    filterOptionHover,
    filterOptionChecked,
    filterHover,
    filterChecked,
  } = useColors();

  const selectStyles = {
    option: {
      bg: filterBg,
      color: inputColor,
    },
    '> option:hover': {
      bg: filterOptionHover + '!important',
    },
    '> option:checked': {
      bg: filterOptionChecked + '!important',
    },
  };

  const selectProps = {
    sx: selectStyles,
    _hover: {
      bg: filterHover,
    },
    _focus: {
      bg: filterChecked,
      borderColor: 'blue.300',
    },
  };

  return (
    <VStack spacing={4} align="stretch">
      <Flex gap={4} flexWrap="wrap">
        <Box flex={2} minW="300px">
          <InputGroup width="100%" maxW="600px">
            <InputLeftAddon p={0} borderRightRadius={0} bg={filterSectionBg}>
              <Select
                value={searchField}
                onChange={(e) => setSearchField(e.target.value)}
                h="100%"
                border="none"
                {...selectProps}
              >
                <option value="all">All</option>
                <option value="sku">SKU</option>
                <option value="marketplace-sku">Item ID / ASIN</option>
                <option value="title">Title</option>
              </Select>
            </InputLeftAddon>
            <Input
              color={inputColor}
              placeholder={`Search ${searchField === 'all' ? '' : searchField.toUpperCase()}`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              borderLeftRadius={0}
            />
            <InputRightElement>
              <IconButton
                icon={<SearchIcon />}
                aria-label="Search"
                size="sm"
                onClick={handleSearch}
              />
            </InputRightElement>
          </InputGroup>
        </Box>
        <HStack spacing={4} flex={1} justify="flex-end">
          <Select
            width="200px"
            placeholder="Listing status"
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            {...selectProps}
          >
            <option value="">All</option>
            <option value="synced">Synced</option>
            <option value="not-synced">Not Synced</option>
            <option value="error">Error</option>
          </Select>
          <Button
            leftIcon={<BsFilter />}
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            colorScheme={showFilters ? 'blue' : 'gray'}
          >
            All filters
          </Button>
          <Button
            leftIcon={<MdDelete />}
            variant="outline"
            onClick={() => setShowDeleteSection(!showDeleteSection)}
            colorScheme={showDeleteSection ? 'red' : 'gray'}
          >
            Delete
          </Button>
        </HStack>
      </Flex>

      {/* Advanced Filters */}
      {showFilters && (
        <Box
          p={4}
          py={8}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="md"
          bg={filterSectionBg}
        >
          <HStack spacing={8} align="start">
            {/* Price Filter */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Price Range
              </Text>
              <HStack spacing={2}>
                <InputGroup size="sm" width="120px">
                  <InputLeftAddon children="$" />
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={filters.priceMin}
                    onChange={(e) => handleFilterChange('priceMin', e.target.value)}
                    color={inputColor}
                    _placeholder={{ color: placeholderColor }}
                  />
                </InputGroup>
                <Text>to</Text>
                <InputGroup size="sm" width="120px">
                  <InputLeftAddon children="$" />
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={filters.priceMax}
                    onChange={(e) => handleFilterChange('priceMax', e.target.value)}
                    color={inputColor}
                    _placeholder={{ color: placeholderColor }}
                  />
                </InputGroup>
              </HStack>
            </Box>

            {/* Quantity Filter */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Quantity Range
              </Text>
              <HStack spacing={2}>
                <InputGroup size="sm" width="100px">
                  <Input
                    type="number"
                    placeholder="Min"
                    value={filters.quantityMin}
                    onChange={(e) => handleFilterChange('quantityMin', e.target.value)}
                    color={inputColor}
                    _placeholder={{ color: placeholderColor }}
                  />
                </InputGroup>
                <Text>to</Text>
                <InputGroup size="sm" width="100px">
                  <Input
                    type="number"
                    placeholder="Max"
                    value={filters.quantityMax}
                    onChange={(e) => handleFilterChange('quantityMax', e.target.value)}
                    color={inputColor}
                    _placeholder={{ color: placeholderColor }}
                  />
                </InputGroup>
              </HStack>
            </Box>

            {/* Marketplace Filter */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Marketplace
              </Text>
              <Select
                size="sm"
                width="200px"
                placeholder="Select marketplace"
                value={filters.marketplace}
                onChange={(e) => handleFilterChange('marketplace', e.target.value)}
                isDisabled={getMarketplacesListStatus === 'pending'}
                {...selectProps}
              >
                <option value="">All Marketplaces</option>
                {marketplaceOptions?.map((option) => (
                  <option key={option.id} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Box>

            {/* Store Filter */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Store
              </Text>
              <Select
                size="sm"
                width="200px"
                placeholder="Select store"
                value={filters.store}
                onChange={(e) => handleFilterChange('store', e.target.value)}
                isDisabled={getStoresListStatus === 'pending'}
                {...selectProps}
              >
                <option value="">All Stores</option>
                {storeOptions?.map((option) => (
                  <option key={option.id} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Box>

            {/* Supplier Filter */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Supplier
              </Text>
              <Select
                size="sm"
                width="200px"
                placeholder="Select supplier"
                value={filters.supplier}
                onChange={(e) => handleFilterChange('supplier', e.target.value)}
                isDisabled={getSuppliersListStatus === 'pending'}
                {...selectProps}
              >
                <option value="">All Suppliers</option>
                <option value="no-supplier">No Supplier</option>
                {supplierOptions?.map((option) => (
                  <option key={option.id} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Box>

            {/* Clear Filters Button */}
            <Button
              size="sm"
              variant="ghost"
              colorScheme="blue"
              onClick={() =>
                handleFilterChange('clear', {
                  priceMin: '',
                  priceMax: '',
                  quantityMin: '',
                  quantityMax: '',
                  marketplace: '',
                  supplier: '',
                  store: '',
                  status: '',
                })
              }
              alignSelf="end"
            >
              Clear filters
            </Button>
          </HStack>
        </Box>
      )}

      {/* Delete Section */}
      {showDeleteSection && (
        <Box
          p={4}
          py={8}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="md"
          bg={filterSectionBg}
        >
          <HStack spacing={8} align="end">
            {/* Store Filter */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Store
              </Text>
              <Select
                size="sm"
                width="300px"
                placeholder="Select store"
                value={deleteFilters.store}
                onChange={(e) => handleDeleteFilterChange('store', e.target.value)}
                isDisabled={getStoresListStatus === 'pending'}
                {...selectProps}
              >
                <option value="">All Stores</option>
                {storeOptions?.map((option) => (
                  <option key={option.id} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Box>

            <Box flex={1} />

            {/* Clear Delete Filters Button */}
            <Button
              size="sm"
              variant="ghost"
              colorScheme="blue"
              onClick={() => handleDeleteFilterChange('clear', { store: '' })}
            >
              Clear filters
            </Button>

            {/* Delete Button */}
            <Button
              size="sm"
              colorScheme="red"
              onClick={handleDeleteByFilter}
              isLoading={isDeleting}
              loadingText="Deleting..."
              isDisabled={!deleteFilters.store}
            >
              Delete Listings
            </Button>
          </HStack>
        </Box>
      )}
    </VStack>
  );
};

export default FilterSection;
